import React, { useMemo } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { Text } from '../Text';
import { Icon } from '../Icon';
import { Button } from '../Button';
import { Menu, MenuItem, MenuLabel, MenuDropdown } from '../Menu';
import { PaginationProps, type PaginationMetrics } from './types';
import { factory } from '../../core/factory';
import { useTheme } from '../../core/theme';
import { useDirection } from '../../core/providers/DirectionProvider';
import { getSpacingStyles, extractSpacingProps, mergeSlotProps } from '../../core/utils';
import { resolveComponentSize, type ComponentSize, type ComponentSizeValue } from '../../core/theme/componentSize';
import { getComponentSize } from '../../core/theme/unified-sizing';

const PAGINATION_ALLOWED_SIZES = ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'] as const;
const PAGINATION_ALLOWED_SIZES_ARRAY: ComponentSize[] = [...PAGINATION_ALLOWED_SIZES];

const MIN_PAGINATION_METRICS = {
  height: 24,
  minWidth: 20,
  paddingHorizontal: 4,
  fontSize: 10,
  iconSize: 10,
  borderRadius: 4,
} as const;

function createMetricsForToken(size: ComponentSize): PaginationMetrics {
  const config = getComponentSize(size);

  return {
    height: config.height,
    minWidth: Math.max(MIN_PAGINATION_METRICS.minWidth, Math.round(config.height * 0.9)),
    paddingHorizontal: Math.max(MIN_PAGINATION_METRICS.paddingHorizontal, Math.round(config.padding * 0.8)),
    fontSize: Math.max(MIN_PAGINATION_METRICS.fontSize, config.fontSize),
    iconSize: Math.max(MIN_PAGINATION_METRICS.iconSize, config.iconSize),
    borderRadius: Math.max(MIN_PAGINATION_METRICS.borderRadius, config.borderRadius),
  };
}

const PAGINATION_SIZE_SCALE: Partial<Record<ComponentSize, PaginationMetrics>> = {
  xs: createMetricsForToken('xs'),
  sm: createMetricsForToken('sm'),
  md: createMetricsForToken('md'),
  lg: createMetricsForToken('lg'),
  xl: createMetricsForToken('xl'),
  '2xl': createMetricsForToken('2xl'),
  '3xl': createMetricsForToken('3xl'),
};

const BASE_PAGINATION_METRICS: PaginationMetrics = PAGINATION_SIZE_SCALE.md ?? createMetricsForToken('md');

function resolvePaginationMetrics(value: ComponentSizeValue | undefined): PaginationMetrics {
  const resolved = resolveComponentSize(value, PAGINATION_SIZE_SCALE, {
    allowedSizes: PAGINATION_ALLOWED_SIZES_ARRAY,
    fallback: 'md',
  });

  if (typeof resolved === 'number') {
    return calculateNumericMetrics(resolved);
  }

  return resolved;
}

function calculateNumericMetrics(height: number): PaginationMetrics {
  const normalizedHeight = Math.max(MIN_PAGINATION_METRICS.height, Math.round(height));
  const scale = normalizedHeight / BASE_PAGINATION_METRICS.height;
  const scaleMetric = (base: number, minimum: number) => Math.max(minimum, Math.round(base * scale));

  return {
    height: normalizedHeight,
    minWidth: scaleMetric(BASE_PAGINATION_METRICS.minWidth, MIN_PAGINATION_METRICS.minWidth),
    paddingHorizontal: scaleMetric(BASE_PAGINATION_METRICS.paddingHorizontal, MIN_PAGINATION_METRICS.paddingHorizontal),
    fontSize: scaleMetric(BASE_PAGINATION_METRICS.fontSize, MIN_PAGINATION_METRICS.fontSize),
    iconSize: scaleMetric(BASE_PAGINATION_METRICS.iconSize, MIN_PAGINATION_METRICS.iconSize),
    borderRadius: scaleMetric(BASE_PAGINATION_METRICS.borderRadius, MIN_PAGINATION_METRICS.borderRadius),
  };
}

export const Pagination = factory<{
  props: PaginationProps;
  ref: View;
}>((props, ref) => {
  const {
    current,
    total,
    siblings = 1,
    boundaries = 1,
    onChange,
    size = 'md',
    variant = 'default',
    color = 'primary',
    showFirst = true,
    showPrevNext = true,
    labels = {},
    disabled = false,
    style,
    buttonStyle,
    activeButtonStyle,
    textStyle,
    activeTextStyle,
    hideOnSinglePage = false,
    showSizeChanger = false,
    pageSizeOptions = [10, 20, 50, 100],
    pageSize = 10,
    onPageSizeChange,
    showTotal = false,
    totalItems,
    labelProps,
    activeLabelProps,
    ...rest
  } = props;

  const theme = useTheme();
  const { isRTL } = useDirection();
  const { spacingProps, otherProps } = extractSpacingProps(rest);
  const spacingStyle = getSpacingStyles(spacingProps);
  const sizeMetrics = useMemo(() => resolvePaginationMetrics(size), [size]);

  if (hideOnSinglePage && total <= 1) {
    return null;
  }

  // Generate page numbers to display
  const generatePageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    
    // Always show first boundary pages
    for (let i = 1; i <= Math.min(boundaries, total); i++) {
      pages.push(i);
    }
    
    // Calculate range around current page
    const startPage = Math.max(current - siblings, boundaries + 1);
    const endPage = Math.min(current + siblings, total - boundaries);
    
    // Add ellipsis if there's a gap between boundaries and current range
    if (startPage > boundaries + 1) {
      pages.push('ellipsis');
    }
    
    // Add pages around current page
    for (let i = startPage; i <= endPage; i++) {
      if (i > boundaries && i <= total - boundaries) {
        pages.push(i);
      }
    }
    
    // Add ellipsis if there's a gap between current range and last boundaries
    if (endPage < total - boundaries) {
      pages.push('ellipsis');
    }
    
    // Always show last boundary pages
    for (let i = Math.max(total - boundaries + 1, boundaries + 1); i <= total; i++) {
      if (i > boundaries) {
        pages.push(i);
      }
    }
    
    return [...new Set(pages)]; // Remove duplicates
  };

  const pages = generatePageNumbers();

  // Get button styles based on variant and state
  const getButtonStyles = (isActive: boolean, isDisabled: boolean) => {
    const baseStyle = {
      height: sizeMetrics.height,
      minWidth: sizeMetrics.minWidth,
      paddingHorizontal: sizeMetrics.paddingHorizontal,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      borderRadius: sizeMetrics.borderRadius,
      marginHorizontal: 2,
    };

    const colorScheme = theme.colors[color] || theme.colors.primary;

    if (isDisabled) {
      return {
        ...baseStyle,
        backgroundColor: variant === 'outline' ? 'transparent' : theme.colors.gray[1],
        borderWidth: variant === 'outline' ? 1 : 0,
        borderColor: theme.colors.gray[3],
        opacity: 0.5,
      };
    }

    if (isActive) {
      switch (variant) {
        case 'outline':
          return {
            ...baseStyle,
            backgroundColor: colorScheme[6],
            borderWidth: 1,
            borderColor: colorScheme[6],
          };
        case 'subtle':
          return {
            ...baseStyle,
            backgroundColor: colorScheme[1],
            borderWidth: 0,
          };
        default:
          return {
            ...baseStyle,
            backgroundColor: colorScheme[6],
            borderWidth: 0,
          };
      }
    }

    switch (variant) {
      case 'outline':
        return {
          ...baseStyle,
          backgroundColor: 'transparent',
          borderWidth: 1,
          borderColor: theme.colors.gray[3],
        };
      case 'subtle':
        return {
          ...baseStyle,
          backgroundColor: 'transparent',
          borderWidth: 0,
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: theme.colors.gray[1],
          borderWidth: 0,
        };
    }
  };

  const getTextColor = (isActive: boolean, isDisabled: boolean) => {
    if (isDisabled) {
      return theme.text.disabled;
    }
    
    if (isActive) {
      return variant === 'outline' || variant === 'default' ? 'white' : theme.colors[color][7];
    }
    
    return theme.text.primary;
  };

  const renderButton = (
    content: React.ReactNode,
    onPress: () => void,
    isActive = false,
    isDisabled = false,
    key?: string | number
  ) => {
    const buttonStyles = getButtonStyles(isActive, isDisabled);
    const textColor = getTextColor(isActive, isDisabled);

    return (
      <TouchableOpacity
        key={key}
        onPress={onPress}
        disabled={disabled || isDisabled}
        style={[
          buttonStyles,
          buttonStyle,
          isActive && activeButtonStyle,
        ]}
        accessibilityRole="button"
      >
        {typeof content === 'string' || typeof content === 'number' ? (
          <Text
            {...mergeSlotProps(
              mergeSlotProps(
                {
                  weight: isActive ? ('600' as const) : ('400' as const),
                  style: [
                    { fontSize: sizeMetrics.fontSize, color: textColor },
                    textStyle,
                    isActive && activeTextStyle,
                  ],
                },
                labelProps
              ),
              isActive ? activeLabelProps : undefined
            )}
          >
            {content}
          </Text>
        ) : (
          content
        )}
      </TouchableOpacity>
    );
  };

  const renderPageButton = (page: number | 'ellipsis', index: number) => {
    if (page === 'ellipsis') {
      return (
        <View
          key={`ellipsis-${index}`}
          style={{
            height: sizeMetrics.height,
            minWidth: sizeMetrics.minWidth,
            justifyContent: 'center',
            alignItems: 'center',
            marginHorizontal: 2,
          }}
        >
          <Text
            style={{
              fontSize: sizeMetrics.fontSize,
              color: theme.text.muted,
            }}
          >
            ...
          </Text>
        </View>
      );
    }

    return renderButton(
      page,
      () => onChange(page),
      current === page,
      false,
      page
    );
  };

  const renderTotal = () => {
    if (!showTotal || !totalItems) return null;

    const startItem = (current - 1) * pageSize + 1;
    const endItem = Math.min(current * pageSize, totalItems);

    if (typeof showTotal === 'function') {
      return (
        <View style={isRTL ? { marginLeft: 16 } : { marginRight: 16 }}>
          {showTotal(totalItems, [startItem, endItem])}
        </View>
      );
    }

    return (
      <View style={isRTL ? { marginLeft: 16 } : { marginRight: 16 }}>
        <Text
          style={{
            fontSize: sizeMetrics.fontSize,
            color: theme.text.secondary,
          }}
        >
          {startItem}-{endItem} of {totalItems}
        </Text>
      </View>
    );
  };

  const renderSizeChanger = () => {
    if (!showSizeChanger || !onPageSizeChange) return null;

    const colorScheme = theme.colors[color] || theme.colors.primary;
    const buttonSize = (typeof size === 'string' && ['xs', 'sm', 'md', 'lg', 'xl'].includes(size)
      ? size
      : 'sm') as 'xs' | 'sm' | 'md' | 'lg' | 'xl';

    return (
      <View style={isRTL ? { marginRight: 12 } : { marginLeft: 12 }}>
        <Menu position="top-end" offset={4}>
          <MenuDropdown>
            <MenuLabel>Rows per page</MenuLabel>
            {pageSizeOptions.map((opt) => (
              <MenuItem
                key={opt}
                onPress={() => {
                  if (opt !== pageSize) onPageSizeChange(opt);
                }}
                startSection={
                  opt === pageSize ? (
                    <Icon name="check" size={14} color={colorScheme[6]} />
                  ) : undefined
                }
              >
                {`${opt} / page`}
              </MenuItem>
            ))}
            {!pageSizeOptions.includes(pageSize) && (
              <MenuItem disabled>{`${pageSize} / page`}</MenuItem>
            )}
          </MenuDropdown>
          <Button
            variant="outline"
            size={buttonSize}
            disabled={disabled}
            endIcon={<Icon name="chevron-down" size={sizeMetrics.iconSize} />}
          >
            {`${pageSize} / page`}
          </Button>
        </Menu>
      </View>
    );
  };

  return (
    <View
      ref={ref}
      style={[
        {
          flexDirection: isRTL ? 'row-reverse' : 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
        },
        spacingStyle,
        style,
      ]}
      {...otherProps}
    >
      {renderTotal()}
      
      {showFirst && renderButton(
        labels.first || <Icon name={isRTL ? 'chevron-right' : 'chevron-left'} size={sizeMetrics.iconSize} />,
        () => onChange(1),
        false,
        current === 1,
        'first'
      )}

      {showPrevNext && renderButton(
        labels.previous || <Icon name={isRTL ? 'chevron-right' : 'chevron-left'} size={sizeMetrics.iconSize} />,
        () => onChange(current - 1),
        false,
        current === 1,
        'prev'
      )}

      {pages.map((page, index) => renderPageButton(page, index))}

      {showPrevNext && renderButton(
        labels.next || <Icon name={isRTL ? 'chevron-left' : 'chevron-right'} size={sizeMetrics.iconSize} />,
        () => onChange(current + 1),
        false,
        current === total,
        'next'
      )}

      {showFirst && renderButton(
        labels.last || <Icon name={isRTL ? 'chevron-left' : 'chevron-right'} size={sizeMetrics.iconSize} />,
        () => onChange(total),
        false,
        current === total,
        'last'
      )}

      {renderSizeChanger()}
    </View>
  );
});

Pagination.displayName = 'Pagination';
